import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Inject, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { RxForDoctorFacade } from '@modules/rx-for-doctor/rx-for-doctor.facade';
import { RxAttachmentsFacade } from '@shared/rx-attachments/rx-attachments.facade';
import { RxAttachmentsVisibiltyEnum } from '@shared/models/enums/rx-attachments-visiblity.enum';
import { RxIntuitiveAttachmentsPanelComponent } from '../../components/rx-intuitive-attachments-panel/rx-intuitive-attachments-panel.component';
import { RxIntuitiveAttachmentsReplacementComponent } from '../../components/rx-intuitive-attachments-replacement/rx-intuitive-attachments-replacement.component';
import { BiSessionInfo } from '@itero/rx-attachments/lib/models/analytics/BiSessionInfo';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { Maybe } from '@/types/globals';
import { WINDOW } from '@shared/tokens';

@Component({
	selector: 'rx-intuitive-attachments',
	templateUrl: './rx-intuitive-attachments.component.html',
	styleUrls: ['./rx-intuitive-attachments.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [RxForDoctorFacade, RxAttachmentsFacade],
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		TlkCardComponent,
		RxIntuitiveAttachmentsReplacementComponent,
		RxIntuitiveAttachmentsPanelComponent
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RxIntuitiveAttachmentsComponent {
	@Output() attachmentsListUpdated = new EventEmitter<void>();

	readonly Visibility = RxAttachmentsVisibiltyEnum;

	attachmentsRxId$ = this.facade.attachmentsRxId$;
	companyId$ = this.facade.companyId$;
	procedureMap$ = this.facade.procedureMap$;
	patientGuid$ = this.facade.patientGuid$;
	patientName$ = this.facade.patientFullName$;
	featureToggles$ = this.facade.featureToggles$;
	companySoftwareOptions$ = this.facade.companySoftwareOptions$;
	contactId$ = this.facade.contactId$;
	userRole$ = this.facade.userRole$;
	isAttachmentsReadOnly$ = this.rxAttachmentsFacade.isAttachmentsReadOnly$;
	isScanner$ = this.rxAttachmentsFacade.isScanner$;
	rxIntuitiveAttachmentsSectionVisibility$ = this.rxAttachmentsFacade.rxIntuitiveAttachmentsSectionVisibility$;
	isDraftSelected$ = this.rxAttachmentsFacade.isDraftSelected$;
	shouldShowIsDraftMessage$ = this.rxAttachmentsFacade.shouldShowIsDraftMessage$;

	biSessionInfo$: Observable<BiSessionInfo> = this.facade.biSessionInfo$;

	timberUrl$: Observable<Maybe<string>> = this.facade.appSettings$.pipe(
		map(({ logging }) => logging?.url)
	);

	constructor(
		private readonly facade: RxForDoctorFacade,
		private readonly rxAttachmentsFacade: RxAttachmentsFacade,
		@Inject(WINDOW) private readonly window: Window
	) {}

	onAttachmentsListUpdated(): void {
		this.attachmentsListUpdated.emit();

		const shouldScroll = this.window.location.href.includes('#attachments-section');
		if (shouldScroll) {
			document.getElementById('attachments-section')?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
		}
	}

	isSafeToCloseUpdated(isSafeToClose: boolean): void {
		this.rxAttachmentsFacade.updateRxAttachmentReadyToSaveState(isSafeToClose);
	}

	updateAttachmentLater(checked: boolean): void {
		this.rxAttachmentsFacade.updateAttachmentLater(checked);
	}
}
